import { useTranslation } from "@/hooks/useTranslation";

export type PreVisitStepIndex = 0 | 1 | 2;

const STEP_CONFIG: readonly {
  indicatorKey: string;
  titleKey: string;
  bodyKey: string;
  /** Shown under a “Next up” line; omitted on last step */
  nextPreviewKey?: string;
  /** Last step: short reassurance instead of “next” */
  footerKey?: string;
}[] = [
  {
    indicatorKey: "preVisitStep1of3",
    titleKey: "preVisitAccessTitle",
    bodyKey: "preVisitAccessBody",
    nextPreviewKey: "preVisitAccessNext",
  },
  {
    indicatorKey: "preVisitStep2of3",
    titleKey: "preVisitLocationTitle",
    bodyKey: "preVisitLocationBody",
    nextPreviewKey: "preVisitLocationNext",
  },
  {
    indicatorKey: "preVisitStep3of3",
    titleKey: "preVisitProofTitle",
    bodyKey: "preVisitProofBody",
  },
];

/**
 * Onboarding-style heading for the scheduled-task pre-visit flow (cleaner portal).
 * Copy comes from static locale JSON via {@link useTranslation} `t`.
 */
export function PreVisitStepHeader({
  stepIndex,
  variant = "default",
}: {
  stepIndex: PreVisitStepIndex;
  variant?: "default" | "turnoverV1";
}) {
  const { t } = useTranslation();
  const cfg = STEP_CONFIG[stepIndex];

  if (variant === "turnoverV1") {
    const stepNum = stepIndex + 1;
    return (
      <header className="mb-5 space-y-3 text-center max-w-md mx-auto px-0">
        <p
          className="text-[11px] font-bold uppercase tracking-[0.12em]"
          style={{ color: "var(--pv-teal)" }}
        >
          {t(cfg.indicatorKey)}
        </p>
        <div className="flex items-center justify-center gap-1.5 pt-0.5">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              className={n === stepNum ? "h-1.5 w-10 rounded-full" : "h-1.5 w-2 rounded-full"}
              style={{
                backgroundColor: n === stepNum ? "var(--pv-teal)" : "var(--pv-line)",
              }}
            />
          ))}
        </div>
        {cfg.nextPreviewKey ? (
          <p className="text-xs leading-relaxed" style={{ color: "var(--pv-ink-3)" }}>
            <span className="font-medium" style={{ color: "var(--pv-ink-2)" }}>
              {t("preVisitNextStepLabel")}
            </span>{" "}
            {t(cfg.nextPreviewKey)}
          </p>
        ) : cfg.footerKey ? (
          <p className="text-xs leading-relaxed" style={{ color: "var(--pv-ink-3)" }}>
            {t(cfg.footerKey)}
          </p>
        ) : null}
        <h1
          className="text-[1.35rem] font-bold leading-tight tracking-tight pt-1"
          style={{ color: "var(--pv-ink)" }}
        >
          {t(cfg.titleKey)}
        </h1>
        <p className="text-sm leading-relaxed px-1" style={{ color: "var(--pv-ink-2)" }}>
          {t(cfg.bodyKey)}
        </p>
      </header>
    );
  }

  return (
    <header className="mb-6 space-y-3 text-center max-w-md mx-auto px-1">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {t(cfg.indicatorKey)}
      </p>
      <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground leading-snug">
        {t(cfg.titleKey)}
      </h1>
      <p className="text-sm text-muted-foreground leading-relaxed">{t(cfg.bodyKey)}</p>
      {cfg.nextPreviewKey ? (
        <p className="text-xs text-muted-foreground leading-relaxed pt-3 border-t border-border/60">
          <span className="font-medium text-foreground/90">{t("preVisitNextStepLabel")}</span>{" "}
          {t(cfg.nextPreviewKey)}
        </p>
      ) : cfg.footerKey ? (
        <p className="text-xs text-muted-foreground leading-relaxed pt-3 border-t border-border/60">
          {t(cfg.footerKey)}
        </p>
      ) : null}
    </header>
  );
}
